<script setup lang="ts">
/**
 * Audit-log viewer. Filters by actor + action + time bounds; expand a row
 * to inspect before/after JSON. Pagination is "limit only" — append to the
 * query string when scrolling to the bottom is implemented.
 */
import { vWrite } from "../admin-read-only";
import { onMounted, ref } from "vue";
import {
  AdminError,
  type AuditRow,
  getAudit,
  purgeRetention,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import { useConfirm } from "../composables/useConfirm";
import SkeletonRows from "./SkeletonRows.vue";

const toast = useToast();
const { confirm } = useConfirm();
function _toastError(e: unknown, context: string): void {
  toast.error(context, e instanceof AdminError ? e.message : String(e));
}

const props = defineProps<{ devUser: string }>();

const rows = ref<AuditRow[]>([]);
const loading = ref(false);
const filterActor = ref("");
const filterAction = ref("");
const fromTs = ref("");
const toTs = ref("");
const limit = ref(200);

const expanded = ref<Set<number>>(new Set());

async function reload() {
  loading.value = true;
  try {
    rows.value = await getAudit(
      {
        actorOid: filterActor.value || undefined,
        action: filterAction.value || undefined,
        fromTs: fromTs.value || undefined,
        toTs: toTs.value || undefined,
        limit: limit.value,
      },
      props.devUser,
    );
  } catch (e) {
    _toastError(e, "Could not load audit log");
  } finally {
    loading.value = false;
  }
}

onMounted(reload);

function toggle(id: number) {
  if (expanded.value.has(id)) {
    expanded.value.delete(id);
  } else {
    expanded.value.add(id);
  }
  expanded.value = new Set(expanded.value); // trigger reactivity
}

async function runPurge() {
  const ok = await confirm({
    title: "Run retention purge?",
    message:
      "Hard-deletes all conversations soft-deleted longer than RETENTION_DAYS ago. This cannot be undone.",
    confirmLabel: "Purge",
    danger: true,
  });
  if (!ok) return;
  try {
    const r = await purgeRetention(props.devUser);
    toast.success(
      `Purged ${r.purged} conversation${r.purged === 1 ? "" : "s"}`,
      `retention window: ${r.retention_days} days`,
    );
    await reload();
  } catch (e) {
    _toastError(e, "Retention purge failed");
  }
}

function shortJson(v: unknown): string {
  if (v === null || v === undefined) return "—";
  const text = JSON.stringify(v);
  return text.length > 80 ? text.slice(0, 77) + "…" : text;
}

/**
 * ADR 0041 — `record_audit` stamps the in-flight correlation id into
 * `extra`, so an audit row can hand you the diagnostic logs for the exact
 * request that performed the mutation. Older rows predate the stamp and
 * simply won't have one.
 */
function requestIdOf(r: AuditRow): string {
  const v = (r.extra as Record<string, unknown> | null)?.request_id;
  return typeof v === "string" ? v : "";
}

function goToLogs(requestId: string) {
  // AdminPage routes on the hash; AdminLogs reads its own filters, so the
  // id goes along as a query param it can pick up.
  window.location.hash = `#/admin?tab=logs&request_id=${encodeURIComponent(requestId)}`;
}
</script>

<template>
  <div class="admin-audit">
    <header class="admin-audit__header">
      <h2>Audit log</h2>
      <button v-write class="btn btn--danger" @click="runPurge">
        Run retention purge
      </button>
    </header>

    <div class="filters">
      <label>
        Actor (oid / email)
        <input v-model="filterActor" placeholder="alice@…" />
      </label>
      <label>
        Action
        <input v-model="filterAction" placeholder="conversation.share" />
      </label>
      <label>
        From
        <input type="datetime-local" v-model="fromTs" />
      </label>
      <label>
        To
        <input type="datetime-local" v-model="toTs" />
      </label>
      <label>
        Limit
        <input type="number" min="10" max="1000" v-model.number="limit" />
      </label>
      <button class="btn btn--primary" @click="reload">Refresh</button>
    </div>

    <table v-if="!loading" class="admin-table">
      <thead>
        <tr>
          <th>When</th>
          <th>Actor</th>
          <th>Action</th>
          <th>Target</th>
          <th>Before / after</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="r in rows" :key="r.id">
          <tr>
            <td class="mono">{{ new Date(r.ts).toLocaleString() }}</td>
            <td>
              {{ r.actor_email ?? r.actor_oid }}
            </td>
            <td>
              <code>{{ r.action }}</code>
            </td>
            <td>
              <span class="kind">{{ r.target_kind }}</span>
              <code class="id">{{ r.target_id }}</code>
            </td>
            <td>
              <span v-if="r.before || r.after" class="muted small">
                before: {{ shortJson(r.before) }} → after:
                {{ shortJson(r.after) }}
              </span>
              <span v-else class="muted small">—</span>
            </td>
            <td class="actions">
              <button
                v-if="r.before || r.after || r.extra"
                class="link"
                :aria-expanded="expanded.has(r.id)"
                :aria-label="`Before and after for ${r.action} on ${r.target_id}`"
                @click="toggle(r.id)"
              >
                {{ expanded.has(r.id) ? "hide" : "details" }}
              </button>
              <button
                v-if="requestIdOf(r)"
                class="link"
                title="Show the diagnostic logs for the request that made this change"
                @click="goToLogs(requestIdOf(r))"
              >
                logs
              </button>
            </td>
          </tr>
          <tr v-if="expanded.has(r.id)" class="audit-detail">
            <td colspan="6">
              <pre
                v-if="r.before"
              ><b>before:</b> {{ JSON.stringify(r.before, null, 2) }}</pre>
              <pre
                v-if="r.after"
              ><b>after:</b> {{ JSON.stringify(r.after, null, 2) }}</pre>
              <pre
                v-if="r.extra"
              ><b>extra:</b> {{ JSON.stringify(r.extra, null, 2) }}</pre>
            </td>
          </tr>
        </template>
        <tr v-if="rows.length === 0">
          <td colspan="6" class="muted">No audit rows match.</td>
        </tr>
      </tbody>
    </table>
    <SkeletonRows v-else :rows="8" />
  </div>
</template>

<style scoped>
.admin-audit {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-audit__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  /* Title plus a danger button: on a phone the button was pinned against
     the right edge with nowhere to go. `.filters` below already wraps. */
  flex-wrap: wrap;
  gap: var(--space-2);
}
.mono {
  font-family: var(--font-mono, monospace);
  white-space: nowrap;
}
.kind {
  display: inline-block;
  margin-right: 0.4rem;
  padding: 0.05rem 0.4rem;
  font-size: 0.78rem;
  font-family: var(--font-mono, monospace);
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
}
.id {
  font-family: var(--font-mono, monospace);
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
.audit-detail pre {
  white-space: pre-wrap;
  font-family: var(--font-mono, monospace);
  font-size: 0.82rem;
  background: var(--color-surface-alt);
  padding: 0.5rem;
  border-radius: var(--radius-sm);
  margin: 0.25rem 0;
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
}
.actions {
  display: flex;
  gap: 0.6rem;
  white-space: nowrap;
}
</style>
